import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  AuthMe,
  exchangeTelegram,
  getMe,
  logout as apiLogout
} from "../api/auth";
import { setOnUnauthorized } from "../utils/api";
import { isTelegramWebApp, getTelegramAuthData } from "../utils/telegram";
import {
  isDefinitiveAuthFailure,
  sleep,
  withTransientRetry
} from "../utils/auth-session";

export type AuthStatus = "loading" | "authed" | "guest";

interface AuthContextValue {
  status: AuthStatus;
  user: AuthMe | null;
  /** Проставляется страницей логина после успешного verify. */
  setUser: (user: AuthMe) => void;
  logout: () => Promise<void>;
  /** Перезапросить /auth/me (например, после линковки провайдера). */
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/** Сколько ждём появления initData из telegram-web-app.js (скрипт может грузиться с defer). */
const TG_INIT_WAIT_MS = 2000;
const TG_INIT_POLL_MS = 50;
/** Фоновые повторы восстановления сессии при сетевых сбоях (не выкидываем на /login). */
const RESTORE_RETRY_MS = 2500;

/**
 * Ждём, пока SDK Telegram отдаст initData, либо станет ясно, что мы не в Mini App.
 */
async function waitForTelegramInitData(): Promise<string> {
  const deadline = Date.now() + TG_INIT_WAIT_MS;
  while (Date.now() < deadline) {
    const { initData } = getTelegramAuthData();
    if (initData) return initData;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tg = typeof window !== "undefined" ? (window as any).Telegram?.WebApp : undefined;
    if (tg) {
      const hasInitData = typeof tg.initData === "string" && tg.initData.length > 0;
      const knownPlatform =
        typeof tg.platform === "string" && tg.platform.length > 0 && tg.platform !== "unknown";
      // SDK есть, но это заглушка в обычном браузере — не ждём дальше.
      if (!hasInitData && !knownPlatform) return "";
    }

    await sleep(TG_INIT_POLL_MS);
  }
  return getTelegramAuthData().initData || "";
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUserState] = useState<AuthMe | null>(null);
  // Защита от StrictMode-двойного запуска инициализации в dev.
  const bootstrapped = useRef(false);
  const restoreTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const becomeGuest = useCallback(() => {
    setUserState(null);
    setStatus("guest");
  }, []);

  const becomeAuthed = useCallback((me: AuthMe) => {
    setUserState(me);
    setStatus("authed");
  }, []);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    const clearRestoreTimer = () => {
      if (restoreTimer.current) {
        clearTimeout(restoreTimer.current);
        restoreTimer.current = null;
      }
    };

    const restoreSession = async (): Promise<boolean> => {
      // 1) Telegram Mini App — exchange; при сбое не сдаёмся сразу, пробуем cookies.
      const initData = await waitForTelegramInitData();
      if (initData || isTelegramWebApp()) {
        const data = initData || getTelegramAuthData().initData;
        if (data) {
          try {
            const me = await withTransientRetry(() => exchangeTelegram(data));
            becomeAuthed(me);
            return true;
          } catch {
            // Exchange упал (сеть / 5xx / редкий 4xx) — ещё есть cookie-сессия.
          }
        }
      }

      // 2) Cookies (веб или fallback после TG).
      const me = await withTransientRetry(() => getMe());
      becomeAuthed(me);
      return true;
    };

    let bootstrapRunning = false;
    const bootstrap = async () => {
      if (bootstrapRunning) return;
      bootstrapRunning = true;
      try {
        await restoreSession();
        clearRestoreTimer();
      } catch (error) {
        if (isDefinitiveAuthFailure(error)) {
          clearRestoreTimer();
          becomeGuest();
          return;
        }
        // Сеть / 5xx: не показываем логин — остаёмся в loading и пробуем снова.
        setStatus("loading");
        clearRestoreTimer();
        restoreTimer.current = setTimeout(() => {
          void bootstrap();
        }, RESTORE_RETRY_MS);
      } finally {
        bootstrapRunning = false;
      }
    };

    void bootstrap();

    return () => clearRestoreTimer();
  }, [becomeAuthed, becomeGuest]);

  // Глобальный 401-обработчик: только когда refresh подтвердил, что сессии нет.
  useEffect(() => {
    setOnUnauthorized(() => {
      becomeGuest();
    });
    return () => setOnUnauthorized(null);
  }, [becomeGuest]);

  const setUser = useCallback(
    (me: AuthMe) => {
      becomeAuthed(me);
    },
    [becomeAuthed]
  );

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // даже если logout не дошёл — локально гасим состояние
    } finally {
      becomeGuest();
    }
  }, [becomeGuest]);

  const refreshMe = useCallback(async () => {
    try {
      const me = await withTransientRetry(() => getMe());
      becomeAuthed(me);
    } catch (error) {
      if (isDefinitiveAuthFailure(error)) {
        becomeGuest();
      }
      // На сетевом сбое оставляем текущий status/user как есть.
    }
  }, [becomeAuthed, becomeGuest]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, setUser, logout, refreshMe }),
    [status, user, setUser, logout, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
};
